// HAEVN Design System — Phase 1 tokens.
//
// Single source of truth for the approved visual system (see design lab
// section N/04–N/09). Import from any component instead of hard-coding
// hex values, font stacks, or radii. Keeping every value here means the
// interior-page migration in Phase 2 can be executed with a single
// import change and no colour hunting.
//
// Every colour lists its WCAG contrast against its intended surface so
// accessibility choices are traceable at read-time.

// ─── Typography ─────────────────────────────────────────────────────
export const fontSans =
  "'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, sans-serif";
export const fontMono =
  "'Geist Mono', ui-monospace, SFMono-Regular, Menlo, monospace";

// ─── Colour ─────────────────────────────────────────────────────────
export const color = {
  // Foundation
  bg: "#FFFFFF",
  surface: "#F4F4F5",          // 1.05:1 vs white — subtle section change
  line: "#E4E4E7",             // hairline borders. No shadows.

  // Ink / text
  ink: "#0B1220",              // 17.9:1 on white ✓ AAA
  inkMuted: "#52525B",         // 7.24:1 on white ✓ AAA
  inkOnDark: "#FFFFFF",        // on ink surface — 17.9:1 ✓ AAA
  inkMutedOnDark: "#B8BCC7",   // on ink surface — 8.2:1 ✓ AAA

  // Accents (functional — never decorative)
  accent: "#0F766E",           // signal teal — 5.13:1 on white ✓ AA
  score: "#166534",            // positive product signals — 7.68:1 ✓ AAA
  gold: "#E5A430",             // reserved for numerals on white / on-ink accents
  alert: "#8A1F1F",            // error only — 5.5:1 on white ✓ AA
  meetup: "#C92F2F",           // Meetup Spots marketing accent — AA on white
  meetupTint: "#FDECED",       // ~8% meetup on white — hover/selection surface
  goose: "#C92F2F",            // alias retained for the Meetup funnel import
  gooseTint: "#FDECED",        // alias retained for the Meetup funnel import
};

// ─── Radius ─────────────────────────────────────────────────────────
export const radius = {
  xs: "2px",
  sm: "4px",     // buttons, inputs
  md: "8px",     // rare — larger cards where warranted
  pill: "999px", // status pills only (never buttons/containers)
};

// ─── Spacing scale (px) ─────────────────────────────────────────────
export const space = [0, 4, 8, 12, 16, 24, 32, 48, 64, 96, 128, 160];

// ─── Layout ─────────────────────────────────────────────────────────
export const layout = {
  maxContent: 1200,   // editorial section max-width
  maxNav: 1280,       // slightly wider so nav breathes at desktop
  gutterMobile: 24,
  gutterDesktop: 40,
};

// ─── Prebuilt style bundles ─────────────────────────────────────────
// Small helpers used by Navigation, HomePage, Footer. Prefer these
// over ad-hoc inline styles so components stay consistent.

export const button = {
  primary: {
    background: color.ink,
    color: "#FFFFFF",
    border: "1px solid transparent",
    padding: "13px 22px",
    borderRadius: radius.sm,
    fontFamily: fontSans,
    fontSize: 15,
    fontWeight: 500,
    letterSpacing: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 200ms ease",
  },
  secondary: {
    background: "transparent",
    color: color.ink,
    border: `1px solid ${color.ink}`,
    padding: "12px 20px",
    borderRadius: radius.sm,
    fontFamily: fontSans,
    fontSize: 15,
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background-color 200ms ease, color 200ms ease",
  },
};

export const status = {
  live: { color: color.score, borderColor: color.score, label: "LIVE" },
  building: { color: color.accent, borderColor: color.accent, label: "NOW BUILDING" },
  soon: { color: color.inkMuted, borderColor: color.inkMuted, label: "COMING SOON" },
};

// Text style presets (as inline-style objects) — for one-off components.
export const text = {
  eyebrow: {
    fontFamily: fontMono,
    fontSize: 12,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    fontWeight: 600,
    color: color.inkMuted,
  },
  mono: {
    fontFamily: fontMono,
    fontSize: 12,
    letterSpacing: "0.06em",
    fontWeight: 500,
    color: color.inkMuted,
    textTransform: "uppercase",
  },
  h1Hero: {
    fontFamily: fontSans,
    fontSize: "clamp(32px, 5.8vw, 68px)",
    lineHeight: 1.05,
    letterSpacing: "-0.025em",
    fontWeight: 500,
  },
  h2Section: {
    fontFamily: fontSans,
    fontSize: "clamp(28px, 3.8vw, 52px)",
    lineHeight: 1.08,
    letterSpacing: "-0.02em",
    fontWeight: 500,
  },
  h3Card: {
    fontFamily: fontSans,
    fontSize: "clamp(20px, 2vw, 24px)",
    lineHeight: 1.25,
    letterSpacing: "-0.01em",
    fontWeight: 500,
  },
  body: {
    fontFamily: fontSans,
    fontSize: 16,
    lineHeight: 1.55,
    fontWeight: 400,
  },
  bodyLead: {
    fontFamily: fontSans,
    fontSize: 18,
    lineHeight: 1.5,
    fontWeight: 400,
  },
};

const tokens = { fontSans, fontMono, color, radius, space, layout, button, status, text };
export default tokens;
